import { useState } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  MapPin, 
  Mail, 
  Phone, 
  Clock, 
  Send, 
  CheckCircle2,
  MessageSquare,
  Building2,
  Headphones
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] }
};

const contactInfo = [
  {
    icon: MapPin,
    title: 'Our Location',
    content: 'Columbus, Ohio',
    details: 'United States',
  },
  {
    icon: Mail,
    title: 'Email Us',
    content: 'support@ulearning.com',
    details: 'We reply within 24 hours',
  },
  {
    icon: Phone,
    title: 'Call Us',
    content: '+1 (614) 555-0123',
    details: 'Mon-Fri 9am-6pm EST',
  },
  {
    icon: Clock,
    title: 'Business Hours',
    content: 'Monday - Friday',
    details: '9:00 AM - 6:00 PM EST',
  },
];

const departments = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'support', label: 'Technical Support' },
  { value: 'billing', label: 'Billing & Payments' },
  { value: 'partnership', label: 'Partnership Opportunities' },
  { value: 'enterprise', label: 'Enterprise Sales' },
  { value: 'press', label: 'Press & Media' },
];

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const initialSubject = searchParams.get('subject') || '';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    department: initialSubject === 'enterprise' ? 'enterprise' : 'general',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitted(true);
    setIsSubmitting(false);
  };

  return (
    <div className="bg-void min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[400px] bg-slate/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[350px] bg-coral/10 rounded-full blur-[100px]" />
        </div>

        <div className="container-luxury relative">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeUp}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full 
                             bg-slate/15 border border-slate/25
                             text-slate-light text-sm font-medium">
                <MessageSquare className="w-4 h-4" />
                <span>Get in Touch</span>
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
              Contact Us
            </h1>

            <p className="mt-4 text-lg text-white/50">
              Have a question or need help? We'd love to hear from you. 
              Our team is ready to assist you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="pb-16">
        <div className="container-luxury">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card text-center"
              >
                <div className="icon-slate mx-auto mb-4">
                  <info.icon className="w-5 h-5 text-slate-light" />
                </div>
                <h3 className="font-semibold text-white mb-1">{info.title}</h3>
                <p className="text-white/70">{info.content}</p>
                <p className="text-white/40 text-sm mt-1">{info.details}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16 border-t border-white/[0.06]">
        <div className="container-luxury">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="accent-line mb-5" />
              <h2 className="text-3xl font-bold text-white mb-4">
                Send us a Message
              </h2>
              <p className="text-white/50 mb-8">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="card text-center py-12"
                >
                  <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8 text-success" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Message Sent!</h3>
                  <p className="text-white/50 max-w-md mx-auto">
                    Thank you for reaching out. Our team will review your message 
                    and respond within 24 hours.
                  </p>
                  <button 
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({
                        name: '',
                        email: '',
                        department: 'general',
                        subject: '',
                        message: '',
                      });
                    }}
                    className="btn-secondary mt-8"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Your Name *</label>
                      <input
                        type="text"
                        className="input"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="label">Email Address *</label>
                      <input
                        type="email"
                        className="input"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label">Department</label>
                    <select
                      className="input"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    >
                      {departments.map((dept) => (
                        <option key={dept.value} value={dept.value}>
                          {dept.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="label">Subject *</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="How can we help you?"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label className="label">Message *</label>
                    <textarea
                      className="input min-h-[150px]"
                      placeholder="Tell us more about your inquiry..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        Send Message
                      </span>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Info & Map */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Map Placeholder */}
              <div className="card p-0 overflow-hidden aspect-[4/3] lg:aspect-auto lg:h-[300px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d195601.07508356714!2d-83.11965595!3d39.96199835!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x883889c1b990de71%3A0xe43266f8cfb1b533!2sColumbus%2C%20OH!5e0!3m2!1sen!2sus!4v1704672000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ulearning Office Location - Columbus, Ohio"
                />
              </div>

              {/* Additional Info Cards */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="card">
                  <div className="flex items-start gap-4">
                    <div className="icon-coral">
                      <Building2 className="w-5 h-5 text-coral" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Corporate Office</h3>
                      <p className="text-white/50 text-sm">
                        Columbus, OH 43215<br />
                        United States
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-start gap-4">
                    <div className="icon-slate">
                      <Headphones className="w-5 h-5 text-slate-light" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Support Team</h3>
                      <p className="text-white/50 text-sm">
                        Available 24/7 for<br />
                        Premium members
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQ Link */}
              <div className="card bg-gradient-to-br from-slate/20 to-slate/5 border-slate/20">
                <h3 className="font-semibold text-white mb-2">Need quick answers?</h3>
                <p className="text-white/50 text-sm mb-4">
                  Check out our Help Center for answers to frequently asked questions.
                </p>
                <Link to="/help" className="btn-secondary inline-flex">
                  Visit Help Center
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}

